import React, { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DateTime } from 'luxon';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { ThemeProvider } from 'styled-components';
import { useScript } from 'usehooks-ts';

import { BaseStyle, BaseStyleEnums, ShortCodeEnums } from '@nhl/nhl-breakaway-ui';
import { useCloseable } from '@nhl/react-hooks/lib/useCloseable';

import commonMessages from '~/core/commonMessages';
import { KeyboardKey } from '~/core/enums';
import { useOptionForLocale } from '~/core/hooks/useOptionForLocale';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import AdTag from '~/views/components/adTag/AdTag';
import { AdType } from '~/views/components/adTag/enums';
import ShareBar from '~/views/components/shareBar/ShareBar';

import Skeleton from './components/skeleton/Skeleton';
import { BrightcoveErrorCode } from './enums';
import { BrightcovePlayer, MediaInfo, VideoMetadata } from './types';

import {
    Date,
    Header,
    InfoContainer,
    MetadataContainer,
    ModalWrapper,
    ShareBarContainer,
    VideoContainer,
    VideoDescription,
    VideoTitle,
} from './styles';

const ACCOUNT_ID = 6415718365001;
const PLAYER_ID = 'EXtG1xJ7H';

// TODO: Remove this once Brightcove fixes the bug that breaks autoplay for videos that shouldn't show pre-roll
const TEMPORARY_SHORT_VIDEO_PLAYER_ID = 'BwZSYBF80a';

type Props = {
    closePlayer?: () => void;
    isInline?: boolean;
    isShortVideo?: boolean;
    showVideoMetadata?: boolean;
    videoId: number | string;
    sharingUrl?: string;
};

const SCRIPT_READY_STATUS = 'ready';

const VideoPlayer: FC<Props> = ({
    videoId,
    isInline,
    isShortVideo,
    closePlayer,
    showVideoMetadata = true,
    sharingUrl,
}) => {
    const size = useSizeWithThrottle();
    const intl = useIntl();

    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const [mediaInfo, setMediaInfo] = useState<MediaInfo>();

    const modalWrapper = useRef<HTMLDivElement | null>(null);
    const videoPlayerNode = useRef<HTMLVideoElement | null>(null);
    const videoJsPlayer = useRef<BrightcovePlayer | null>(null);

    const playerId = useMemo(() => (isShortVideo ? TEMPORARY_SHORT_VIDEO_PLAYER_ID : PLAYER_ID), [isShortVideo]);

    const brightcoveScriptStatus = useScript(
        `//players.brightcove.net/${ACCOUNT_ID}/${playerId}_default/index.min.js`,
        {
            removeOnUnmount: false,
        }
    );

    const brightcoveInstance = useMemo(
        () => (brightcoveScriptStatus === SCRIPT_READY_STATUS ? window.bc?.[`${playerId}_default`] : undefined),
        [brightcoveScriptStatus, playerId]
    );

    const _closePlayer = useCallback(() => {
        videoJsPlayer.current?.dispose();
        closePlayer && closePlayer();
    }, [closePlayer]);

    const lockFocusForModal = useCallback(() => {
        const controls: HTMLElement | undefined = videoJsPlayer.current?.controlBar.contentEl() as HTMLElement;

        if (controls) {
            // make sure the controls are tabbable. we want tabindex to be 0 rather than larger
            // numbers: https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/tabindex
            controls.childNodes?.forEach((el: HTMLElement) => {
                // several elements are layered on top of each other in the control panel, and this ensures that only
                // the ones with actual accessibility provisions/functionality are tabbable
                if (el.ariaLabel || el.title) {
                    el.setAttribute('tabindex', '0');
                }
            });

            const firstFocusableElement = controls.childNodes[0] as HTMLElement;
            const lastFocusableElement = controls.childNodes[controls?.childNodes?.length - 1] as HTMLElement;

            controls.addEventListener(
                'keydown',
                (e) => {
                    const isTabPressed = e.key === KeyboardKey.TAB;

                    if (e.key === KeyboardKey.ESCAPE) {
                        _closePlayer();
                    }

                    if (!isTabPressed) {
                        return;
                    }

                    // swallow inputs while ad is playing
                    const adControlBar = document.querySelector('.vjs-ad-control-bar'); // class that's only applied to control bar when ad playing
                    if (adControlBar !== null) {
                        e.preventDefault();
                        return;
                    }

                    if (e.shiftKey) {
                        // if shift key is also pressed (thus tabbing through in reverse order)
                        if (document.activeElement === firstFocusableElement) {
                            lastFocusableElement?.focus(); // move focus to the last focusable element
                            e.preventDefault();
                        }
                    } else {
                        // if only tab key is pressed
                        if (document.activeElement === lastFocusableElement) {
                            firstFocusableElement?.focus(); // move focus to the first focusable element
                            e.preventDefault();
                        }
                    }
                },
                true
            );
        }
    }, [_closePlayer]);

    useEffect(() => {
        if (isInitialized || videoPlayerNode.current === null || brightcoveInstance === undefined) return;

        videoJsPlayer.current = brightcoveInstance(videoPlayerNode.current);

        videoJsPlayer.current?.errors({
            errors: {
                [BrightcoveErrorCode.MEDIA_ERR_DECODE]: {
                    headline: intl.formatMessage(commonMessages.videoMessage.genericErrorHeader),
                    message: intl.formatMessage({
                        description: 'Error message for when a video cannot be decoded by the browser.',
                        defaultMessage:
                            'This video cannot be played due to features this browser does not support. You may consider trying to watch in a different browser.',
                    }),
                },
                [BrightcoveErrorCode.MEDIA_ERR_SRC_NOT_SUPPORTED]: {
                    headline: intl.formatMessage(commonMessages.videoMessage.genericErrorHeader),
                    message: intl.formatMessage({
                        description: 'Error message for when a video is not supported in the current browser.',
                        defaultMessage:
                            'This video is unavailable or cannot be played due to features this browser does not support.',
                    }),
                },
                [BrightcoveErrorCode.PLAYER_ERR_GEO_RESTRICTED]: {
                    headline: intl.formatMessage({
                        description:
                            'Header for a video error message describing a video whose playback is geographically restricted.',
                        defaultMessage: 'This video is geo-restricted.',
                    }),
                    message: intl.formatMessage({
                        description: "Error message for when a video's playback is geo-restricted.",
                        defaultMessage: 'This video is not available to watch based on your detected location.',
                    }),
                },
            },
        });

        // if video player is a modal, autofocus the player on load and trap focus in the player controls
        if (!isInline) {
            videoPlayerNode.current.setAttribute('tabindex', '0');
            videoPlayerNode.current.focus();

            videoJsPlayer.current?.on('loadstart', () => {
                lockFocusForModal();
                setMediaInfo(videoJsPlayer.current?.mediainfo);
            });
        }

        setIsInitialized(true);
    }, [brightcoveInstance, intl, isInitialized, isInline, lockFocusForModal]);

    useCloseable({
        closeableRef: modalWrapper,
        closeFunction: _closePlayer,
        shouldStopListening: isInline,
    });

    const videoNode: ReactNode = useMemo(
        () => (
            <video
                ref={videoPlayerNode}
                data-account={ACCOUNT_ID}
                data-video-id={videoId}
                data-player={playerId}
                data-embed="default"
                className="video-js"
                controls
            />
        ),
        [playerId, videoId]
    );

    const videoMetadata = useOptionForLocale<VideoMetadata>({
        [ShortCodeEnums.Language.English]: {
            name: mediaInfo?.name || '',
            description: mediaInfo?.description || '',
            longDescription: mediaInfo?.long_description || '',
            slug: mediaInfo?.custom_fields?.slug || '',
        },
        ...mediaInfo?.variants?.reduce<Record<ShortCodeEnums.Language, VideoMetadata>>((options, variant) => {
            options[variant.language] = {
                name: variant.custom_fields.headline,
                description: variant.description,
                longDescription: variant.long_description,
                slug: variant.custom_fields.slug,
            };
            return options;
        }, {} as Record<ShortCodeEnums.Language, VideoMetadata>),
    });

    const shareBar: ReactNode = useMemo(
        () =>
            videoMetadata && sharingUrl ? (
                <ShareBar
                    sharingUrl={sharingUrl}
                    title={videoMetadata.name}
                    embedString={`https://players.brightcove.net/${ACCOUNT_ID}/${playerId}_default/index.html?videoId=${videoId}`}
                />
            ) : null,
        [playerId, sharingUrl, videoId, videoMetadata]
    );

    const brandingLogo = useOptionForLocale({
        [ShortCodeEnums.Language.English]: 'https://wsr.nhle.com/images/nhl_shield_wm_on_dark.svg',
        [ShortCodeEnums.Language.French]: 'https://wsr.nhle.com/images/lnh_shield_wm_on_dark.svg',
    });

    const nodeToRender: ReactNode = (
        <VideoContainer $isInline={isInline}>
            {!isInline ? (
                <ModalWrapper ref={modalWrapper}>
                    <Header>
                        <img src={brandingLogo} />
                        {window.__AD_SETTINGS__.presbyDivId && (
                            <AdTag adType={AdType.PRESBY} divId={window.__AD_SETTINGS__.presbyDivId} />
                        )}
                    </Header>
                    {videoNode}
                    {showVideoMetadata && (
                        <ThemeProvider theme={BaseStyle.DarkTheme}>
                            <InfoContainer>
                                {videoMetadata && videoMetadata.name !== '' ? (
                                    <>
                                        <MetadataContainer>
                                            <VideoTitle>{videoMetadata.name}</VideoTitle>
                                            <VideoDescription>{videoMetadata.longDescription}</VideoDescription>

                                            {size.width <= BaseStyleEnums.ScreenWidthBreakpoints.medium && shareBar}

                                            <Date>
                                                {mediaInfo?.createdAt &&
                                                    DateTime.fromISO(mediaInfo?.createdAt).toLocaleString(
                                                        DateTime.DATE_FULL
                                                    )}
                                            </Date>
                                        </MetadataContainer>
                                        {size.width > BaseStyleEnums.ScreenWidthBreakpoints.medium && (
                                            <ShareBarContainer>{shareBar}</ShareBarContainer>
                                        )}
                                    </>
                                ) : (
                                    <Skeleton />
                                )}
                            </InfoContainer>
                        </ThemeProvider>
                    )}
                    {window.__AD_SETTINGS__.leaderDivIds.length > 0 && (
                        <AdTag adType={AdType.LEADER} divId={window.__AD_SETTINGS__.leaderDivIds[0]} forceReload />
                    )}
                </ModalWrapper>
            ) : (
                videoNode
            )}
        </VideoContainer>
    );

    return <>{isInline ? nodeToRender : createPortal(nodeToRender, document.body)}</>;
};

export default VideoPlayer;
